// Date and period-key helpers. Pure functions, no React.
//
// Storage convention:
//   month key:  YYYY-MM  ("2026-03")
//   week key:   YYYY-Www (ISO week, e.g. "2026-W11")
//
// Activity dates in transactions are stored as MM/DD/YYYY strings —
// parseDate is the canonical parser. Everything else in this module
// reduces to parseDate + date arithmetic.

// Parse the app's canonical date string ("MM/DD/YYYY") into a Date.
// Returns null on anything that doesn't match — callers must guard.
export const parseDate = (dateStr) => {
  if (!dateStr || !String(dateStr).includes('/')) return null;
  const [m, d, y] = String(dateStr).split('/').map(Number);
  if (!m || !d || !y) return null;
  return new Date(y, m - 1, d);
};

// "MM/DD/YYYY" → "YYYY-MM". "unknown" when the input isn't parseable so
// downstream Set/sort logic stays well-defined.
export const monthKey = (dateStr) => {
  if (!dateStr) return 'unknown';
  const parts = String(dateStr).split('/');
  if (parts.length !== 3) return 'unknown';
  return `${parts[2]}-${parts[0].padStart(2,'0')}`;
};

// "YYYY-MM" → "Mar 2026". Used in period selectors and chart labels.
export const monthLabel = (key) => {
  if (!key || key === 'unknown') return 'Unknown';
  const [y, m] = key.split('-');
  const names = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${names[parseInt(m)-1]} ${y}`;
};

// ISO week: weeks start Monday. The Thursday of the week determines the
// year (so a date in early January can belong to the previous year's
// last ISO week).
export const weekKey = (dateStr) => {
  const dt = parseDate(dateStr);
  if (!dt) return 'unknown';
  return weekKeyFromDate(dt);
};

// Same ISO-week math but starting from a JS Date instead of a string.
export const weekKeyFromDate = (dt) => {
  const target = new Date(dt.valueOf());
  const dayNr = (dt.getDay() + 6) % 7; // 0=Mon..6=Sun
  target.setDate(target.getDate() - dayNr + 3);
  const firstThursday = new Date(target.getFullYear(), 0, 4);
  const diff = target - firstThursday;
  const weekNr = 1 + Math.round(diff / (7 * 24 * 3600 * 1000));
  return `${target.getFullYear()}-W${String(weekNr).padStart(2, '0')}`;
};

// "YYYY-Www" → { monday, sunday } JS Dates for the week's start/end.
export const weekBounds = (weekKeyStr) => {
  if (!weekKeyStr || weekKeyStr === 'unknown') return null;
  const [y, wPart] = weekKeyStr.split('-W');
  const year = Number(y), week = Number(wPart);
  // Jan 4 is always in week 1
  const jan4 = new Date(year, 0, 4);
  const dayNr = (jan4.getDay() + 6) % 7;
  const monday = new Date(jan4);
  monday.setDate(jan4.getDate() - dayNr + (week - 1) * 7);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return { monday, sunday };
};

// "Week 11 (Mar 9 – Mar 15)" — full label for selectors and chart axes.
export const weekLabel = (weekKeyStr) => {
  if (!weekKeyStr || weekKeyStr === 'unknown') return 'Unknown';
  const [, wPart] = weekKeyStr.split('-W');
  const b = weekBounds(weekKeyStr);
  if (!b) return weekKeyStr;
  const fmtDay = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return `Week ${Number(wPart)} (${fmtDay(b.monday)} – ${fmtDay(b.sunday)})`;
};

// "Week 11" — short label used where horizontal space is tight.
export const weekShortLabel = (weekKeyStr) => {
  if (!weekKeyStr || weekKeyStr === 'unknown') return 'Unknown';
  const [, wPart] = weekKeyStr.split('-W');
  return `Week ${Number(wPart)}`;
};

// Next N ISO week keys starting with the current week. Used to seed
// future weeks in the period selector so the user can plan ahead even
// before any transactions land in that week.
export const upcomingWeekKeys = (n) => {
  const out = [];
  const today = new Date();
  const monday = new Date(today);
  monday.setDate(today.getDate() - ((today.getDay() + 6) % 7));
  for (let i = 0; i < n; i++) {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i * 7);
    out.push(weekKeyFromDate(d));
  }
  return out;
};
